import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import { Box, ClickAwayListener, Paper, Typography, IconButton, useMediaQuery, useTheme } from '@mui/material';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import { useChat } from '../../contexts/ChatContext';
import { useVideoCall } from '../../hooks/useVideoCall';
import ChatFabButton from './ChatFabButton';
import ChatPanel from './ChatPanel';
import ChatWindow from './ChatWindow';
import VideoCallModal from './VideoCallModal';
import IncomingCallModal from './IncomingCallModal';
import GroupVideoCallModal from './GroupVideoCallModal';

async function getLocalStream() {
  try {
    const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : null;
    if (!md?.getUserMedia) {
      const legacy =
        typeof navigator !== 'undefined' &&
        (navigator.getUserMedia || navigator.webkitGetUserMedia || navigator.mozGetUserMedia);
      if (legacy) {
        return await new Promise((resolve, reject) => {
          legacy.call(navigator, { video: true, audio: true }, resolve, reject);
        });
      }
      console.warn('[Chat] getUserMedia não disponível. Use HTTPS e permita acesso à câmera.');
      return null;
    }
    return await md.getUserMedia({ video: true, audio: true });
  } catch (e) {
    console.warn('[Chat] getUserMedia failed:', e);
    return null;
  }
}

/**
 * Widget de chat — Dual-Pane (isExpanded) + MSN Dock + FAB.
 * VideoCall / WebRTC permanece intacto abaixo.
 */
export default function ChatWidget() {
  const {
    connected,
    status,
    setStatus,
    contacts,
    presenceMap,
    loadingContacts,
    openConversations,
    openConversation,
    openGroupConversation,
    createGroupConversation,
    groups,
    loadingGroups,
    fetchGroups,
    closeConversation,
    messagesByConv,
    sendMessage,
    markRead,
    typingMap,
    sendTypingStart,
    sendTypingStop,
    socket,
    user,
    fotoPerfil,
    isChatOpen,
    setChatOpen,
    unreadCount,
    isExpanded,
    setIsExpanded,
    activeChatId,
    setActiveChatId
  } = useChat();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fabRef = useRef(null);

  const {
    localStream: videoLocalStream,
    remoteStream: videoRemoteStream,
    callState: videoCallState,
    remoteUser: videoRemoteUser,
    incomingFrom: videoIncomingFrom,
    startCall,
    acceptCall,
    rejectCall,
    hangup: videoHangup,
    isOpen: videoCallOpen
  } = useVideoCall(socket, user?.id);

  const [groupVideoCall, setGroupVideoCall] = React.useState({
    open: false,
    conversationId: null,
    groupName: null,
    participants: [],
    isCreator: false,
    localStream: null
  });

  const [activeGroupCalls, setActiveGroupCalls] = React.useState({});
  const [windowState, setWindowState] = React.useState({});
  const defaultSize = { minimized: false, width: 420, height: 520 };
  const getWindowState = (convId) => windowState[convId] || defaultSize;

  const setMinimized = useCallback((convId, minimized) => {
    setWindowState((prev) => ({
      ...prev,
      [convId]: { ...(prev[convId] || defaultSize), minimized }
    }));
  }, []);

  const setWindowSize = useCallback((convId, width, height) => {
    setWindowState((prev) => ({
      ...prev,
      [convId]: { ...(prev[convId] || defaultSize), width, height }
    }));
  }, []);

  useEffect(() => {
    if (!socket || !user) return;
    const onCreate = (data) => {
      const cid = data?.conversationId;
      if (!cid) return;
      setActiveGroupCalls((prev) => ({
        ...prev,
        [cid]: {
          creatorId: data.fromUserId,
          creatorName: data.fromUserName,
          participants: [{ userId: data.fromUserId, userName: data.fromUserName }]
        }
      }));
    };
    const onJoin = (data) => {
      const cid = data?.conversationId;
      if (!cid) return;
      const p = { userId: data.fromUserId, userName: data.fromUserName || 'Participante' };
      setActiveGroupCalls((prev) => {
        const cur = prev[cid];
        if (!cur) return prev;
        const exists = (cur.participants || []).some((x) => x.userId === p.userId);
        if (exists) return prev;
        return {
          ...prev,
          [cid]: { ...cur, participants: [...(cur.participants || []), p] }
        };
      });
      setGroupVideoCall((prev) => {
        if (prev.conversationId !== cid) return prev;
        const exists = (prev.participants || []).some((x) => x.userId === p.userId);
        if (exists) return prev;
        return { ...prev, participants: [...(prev.participants || []), p] };
      });
    };
    const onLeave = (data) => {
      const cid = data?.conversationId;
      if (!cid) return;
      const uid = data.fromUserId;
      setActiveGroupCalls((prev) => {
        const cur = prev[cid];
        if (!cur) return prev;
        const nextParts = (cur.participants || []).filter((x) => x.userId !== uid);
        if (nextParts.length === 0) {
          const next = { ...prev };
          delete next[cid];
          return next;
        }
        return { ...prev, [cid]: { ...cur, participants: nextParts } };
      });
      setGroupVideoCall((prev) => {
        if (prev.conversationId !== cid) return prev;
        const nextParts = (prev.participants || []).filter((x) => x.userId !== uid);
        return { ...prev, participants: nextParts };
      });
    };
    socket.on('groupcall:create', onCreate);
    socket.on('groupcall:join', onJoin);
    socket.on('groupcall:leave', onLeave);
    return () => {
      socket.off('groupcall:create', onCreate);
      socket.off('groupcall:join', onJoin);
      socket.off('groupcall:leave', onLeave);
    };
  }, [socket, user]);

  useEffect(() => {
    if (!isExpanded) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setIsExpanded(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isExpanded, setIsExpanded]);

  const handleSelectContact = async (contact) => {
    await openConversation(contact);
    if (!isExpanded) {
      // modo flutuante: mantém janelas MSN
    }
  };

  const handleSelectGroup = async (g) => {
    await openGroupConversation(g);
    if (!isExpanded) setChatOpen(false);
  };

  const openExpanded = useCallback(() => {
    setIsExpanded(true);
    setChatOpen(false);
  }, [setIsExpanded, setChatOpen]);

  const closeExpanded = useCallback(() => {
    setIsExpanded(false);
  }, [setIsExpanded]);

  const handleStartVideoCall = async (contact) => {
    const ok = await startCall(contact);
    if (ok) {
      setChatOpen(false);
      setIsExpanded(false);
    }
  };

  const handleCloseVideoCall = () => {
    videoHangup();
  };

  const handleStartGroupVideoCall = useCallback(
    async (conversationId, groupName) => {
      if (!socket || !user) return;
      const stream = await getLocalStream();
      setGroupVideoCall({
        open: true,
        conversationId,
        groupName,
        participants: [{ userId: user.id, userName: user.nome || user.email || 'Você' }],
        isCreator: true,
        localStream: stream
      });
      setActiveGroupCalls((prev) => ({
        ...prev,
        [conversationId]: {
          creatorId: user.id,
          creatorName: user.nome || user.email,
          participants: [{ userId: user.id, userName: user.nome || user.email }]
        }
      }));
      socket.emit('groupcall:create', { conversationId });
      setChatOpen(false);
      setIsExpanded(false);
    },
    [socket, user, setChatOpen, setIsExpanded]
  );

  const handleJoinGroupVideoCall = useCallback(
    async (conversationId, groupName) => {
      if (!socket || !user) return;
      const stream = await getLocalStream();
      const me = { userId: user.id, userName: user.nome || user.email || 'Você' };
      const existing = activeGroupCalls[conversationId]?.participants || [];
      const merged = existing.some((p) => p.userId === user.id) ? existing : [...existing, me];
      setGroupVideoCall({
        open: true,
        conversationId,
        groupName,
        participants: merged,
        isCreator: false,
        localStream: stream
      });
      socket.emit('groupcall:join', { conversationId });
      setChatOpen(false);
      setIsExpanded(false);
    },
    [socket, user, activeGroupCalls, setChatOpen, setIsExpanded]
  );

  const handleLeaveGroupVideoCall = useCallback(() => {
    const cid = groupVideoCall.conversationId;
    if (groupVideoCall.localStream) {
      groupVideoCall.localStream.getTracks().forEach((t) => t.stop());
    }
    if (socket && cid) {
      socket.emit('groupcall:leave', { conversationId: cid });
    }
    setGroupVideoCall({
      open: false,
      conversationId: null,
      groupName: null,
      participants: [],
      isCreator: false,
      localStream: null
    });
  }, [socket, groupVideoCall.conversationId, groupVideoCall.localStream]);

  const togglePanel = () => {
    if (isExpanded) {
      setIsExpanded(false);
      return;
    }
    setChatOpen((o) => !o);
  };

  const activeConv = useMemo(
    () => openConversations.find((c) => c.id === activeChatId) || null,
    [openConversations, activeChatId]
  );

  const floatingOpen = useMemo(
    () => openConversations.filter((c) => !getWindowState(c.id).minimized),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openConversations, windowState]
  );

  const dockMinimized = useMemo(
    () => openConversations.filter((c) => getWindowState(c.id).minimized),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openConversations, windowState]
  );

  const buildWindowProps = (conv, { embedded = false, minimized = false } = {}) => {
    const state = getWindowState(conv.id);
    return {
      conversationId: conv.id,
      contact: conv.contact,
      isGroup: conv.type === 'group',
      groupName: conv.name,
      messages: messagesByConv[conv.id] || [],
      currentUserId: user?.id,
      typingLabel: typingMap[conv.id],
      onClose: () => closeConversation(conv.id),
      onSendMessage: sendMessage,
      markRead: (messageIds) => markRead(conv.id, messageIds),
      sendTypingStart,
      sendTypingStop,
      onStartVideoCall: handleStartVideoCall,
      onStartGroupVideoCall: handleStartGroupVideoCall,
      activeGroupCall: activeGroupCalls[conv.id],
      onJoinGroupVideoCall: handleJoinGroupVideoCall,
      inGroupCall: groupVideoCall.open && groupVideoCall.conversationId === conv.id,
      currentUser: user,
      fotoPerfil,
      contactStatus: presenceMap[conv.contact?.id]?.status || 'offline',
      currentUserStatus: status,
      minimized,
      onMinimize: embedded ? undefined : () => setMinimized(conv.id, !state.minimized),
      width: state.width,
      height: state.height,
      onResize: embedded ? undefined : (w, h) => setWindowSize(conv.id, w, h),
      embedded,
      onExpand: embedded
        ? undefined
        : () => {
            setActiveChatId(conv.id);
            openExpanded();
          }
    };
  };

  return (
    <>
      {/* Dual-Pane Overlay — Centro de Comunicações */}
      {isExpanded && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(15,23,42,0.55)',
            zIndex: 1300,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: { xs: 0, sm: 2 }
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeExpanded();
          }}
        >
          <Paper
            elevation={8}
            sx={{
              width: { xs: '100%', sm: '92vw' },
              height: { xs: '100%', sm: '90vh' },
              maxWidth: 1400,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: { xs: 0, sm: 2 },
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                flexShrink: 0
              }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Centro de Comunicações
              </Typography>
              <IconButton size="small" onClick={closeExpanded} aria-label="Fechar centro">
                <CloseFullscreenIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
              <Box
                sx={{
                  width: { xs: activeConv ? 0 : '100%', sm: '32%', md: '30%' },
                  display: { xs: activeConv ? 'none' : 'flex', sm: 'flex' },
                  flexDirection: 'column',
                  minWidth: 0,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <ChatPanel
                  isOpen
                  onClose={closeExpanded}
                  hideClose
                  onExpand={undefined}
                  status={status}
                  setStatus={setStatus}
                  connected={connected}
                  contacts={contacts}
                  presenceMap={presenceMap}
                  loadingContacts={loadingContacts}
                  onSelectContact={handleSelectContact}
                  groups={groups}
                  loadingGroups={loadingGroups}
                  onSelectGroup={handleSelectGroup}
                  createGroupConversation={createGroupConversation}
                  openGroupConversation={openGroupConversation}
                  fetchGroups={fetchGroups}
                  user={user}
                  fotoPerfil={fotoPerfil}
                />
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: { xs: activeConv ? 'flex' : 'none', sm: 'flex' },
                  flexDirection: 'column',
                  bgcolor: 'grey.50'
                }}
              >
                {activeConv ? (
                  <ChatWindow {...buildWindowProps(activeConv, { embedded: true })} />
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 3
                    }}
                  >
                    <Typography color="text.secondary" align="center">
                      Selecione um contato ou grupo à esquerda para iniciar a conversa.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Janelas flutuantes abertas (não minimizadas) — só fora do Dual-Pane */}
      {!isExpanded && floatingOpen.length > 0 && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 24,
            display: 'flex',
            flexDirection: 'row-reverse',
            gap: 2,
            zIndex: 1305,
            alignItems: 'flex-end',
            pointerEvents: 'none',
            '& > *': { pointerEvents: 'auto' }
          }}
        >
          {floatingOpen.map((conv) => (
            <ChatWindow key={conv.id} {...buildWindowProps(conv, { minimized: false })} />
          ))}
        </Box>
      )}

      {/* MSN Dock — conversas minimizadas */}
      {!isExpanded && dockMinimized.length > 0 && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 88,
            display: 'flex',
            flexDirection: 'row-reverse',
            gap: 1,
            zIndex: 1299,
            alignItems: 'flex-end',
            maxWidth: 'calc(100vw - 120px)',
            overflowX: 'auto'
          }}
        >
          {dockMinimized.map((conv) => (
            <ChatWindow key={`dock-${conv.id}`} {...buildWindowProps(conv, { minimized: true })} />
          ))}
        </Box>
      )}

      {/* Painel flutuante (lista) — só fora do Dual-Pane */}
      {!isExpanded && isChatOpen && (
        <ClickAwayListener
          mouseEvent="onMouseDown"
          touchEvent="onTouchStart"
          onClickAway={(e) => {
            if (fabRef.current && fabRef.current.contains(e.target)) return;
            setChatOpen(false);
          }}
        >
          <Box
            sx={{
              position: 'fixed',
              bottom: isMobile ? 0 : 90,
              right: isMobile ? 0 : 24,
              width: isMobile ? '100%' : 340,
              height: isMobile ? '100%' : 480,
              maxHeight: isMobile ? '100%' : 'calc(100vh - 120px)',
              zIndex: 1300
            }}
          >
            <ChatPanel
              isOpen={isChatOpen}
              onClose={() => setChatOpen(false)}
              onExpand={openExpanded}
              status={status}
              setStatus={setStatus}
              connected={connected}
              contacts={contacts}
              presenceMap={presenceMap}
              loadingContacts={loadingContacts}
              onSelectContact={handleSelectContact}
              groups={groups}
              loadingGroups={loadingGroups}
              onSelectGroup={handleSelectGroup}
              createGroupConversation={createGroupConversation}
              openGroupConversation={openGroupConversation}
              fetchGroups={fetchGroups}
              user={user}
              fotoPerfil={fotoPerfil}
            />
          </Box>
        </ClickAwayListener>
      )}

      <Box ref={fabRef} sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1310 }}>
        <ChatFabButton isOpen={isChatOpen || isExpanded} unreadCount={unreadCount} onClick={togglePanel} />
      </Box>

      {/* WebRTC — intacto */}
      <IncomingCallModal
        open={videoCallState === 'incoming'}
        fromUser={videoIncomingFrom}
        onAccept={acceptCall}
        onReject={rejectCall}
      />

      <VideoCallModal
        open={videoCallOpen && videoCallState !== 'incoming'}
        remoteUser={videoRemoteUser}
        localStream={videoLocalStream}
        remoteStream={videoRemoteStream}
        callState={videoCallState}
        onClose={handleCloseVideoCall}
        onHangup={handleCloseVideoCall}
      />

      <GroupVideoCallModal
        open={groupVideoCall.open}
        groupName={groupVideoCall.groupName}
        participants={groupVideoCall.participants}
        currentUserId={user?.id}
        isCreator={groupVideoCall.isCreator}
        localStream={groupVideoCall.localStream}
        onClose={handleLeaveGroupVideoCall}
        onHangup={handleLeaveGroupVideoCall}
      />
    </>
  );
}
